import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  XIcon,
} from "@heroicons/react/outline";
import { StarIcon } from "@heroicons/react/solid";
import React, { Fragment, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import axiosInstance from "../../../../services/Interceptor";
import OrderTimeline, { formatOrderDate } from "./OrderTimeline";

const TOAST = {
  position: "top-right",
  autoClose: 2000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
};

function errorMessage(error) {
  const data = error.response && error.response.data;
  if (data && typeof data === "object") {
    const first = Object.values(data)[0];
    return Array.isArray(first) ? first[0] : String(first);
  }
  return error.message;
}

/* five stars; `value` stars are filled, clicking one sets the rating */
function StarRating({ value, onChange, className = "h-6 sm:h-9 w-6 sm:w-9" }) {
  return (
    <div className="flex justify-start items-center gap-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          type="button"
          key={star}
          onClick={() => onChange(star)}
          aria-label={`${star} star${star > 1 ? "s" : ""}`}
        >
          <StarIcon
            className={`${className} stroke-1 stroke-[#FFB800] hover:text-[#FFD600] ${
              star <= value ? "text-[#FFD600]" : "text-white"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function ReceivedOrderDetails() {
  let { id } = useParams();
  const [orderDetails, setOrderDetails] = useState([]);
  const [subtotal, setSubtotal] = useState();
  const baseURL = process.env.REACT_APP_BASE_URL;

  /* order details */
  useEffect(() => {
    axiosInstance
      .get(`orders/api/v1/orders/customer/${id}/`)
      .then((res) => {
        // console.log(res.data);
        setOrderDetails(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  /* subtotal */
  useEffect(() => {
    if (orderDetails) {
      const Total = orderDetails.order_items?.reduce(
        (prev, current) => (prev = prev + current.total_price),
        0
      );
      setSubtotal(Total);
    }
  }, [orderDetails]);

  /* total order fee */
  const total =
    subtotal + parseInt(orderDetails.shipping_cost) - orderDetails.discount;


  /* ratings: stars picked on each product card, and what the customer already submitted */
  const [ratings, setRatings] = useState({});
  const [myReviews, setMyReviews] = useState({});
  const [open, setOpen] = useState(false);
  const [reviewItem, setReviewItem] = useState(null);
  const [modalRating, setModalRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const { register, handleSubmit, reset } = useForm();
  const [productReviewData, setProductReviewData] = useState();

  const deliveredOn = formatOrderDate(
    (orderDetails.status_history || [])
      .filter((entry) => entry.status === "DELIVERED")
      .map((entry) => entry.at)
      .pop()
  );

  /* the customer's existing rating of each product in this order */
  useEffect(() => {
    const productIds = [
      ...new Set((orderDetails.order_items || []).map((item) => item.product_id)),
    ];
    productIds.forEach((productId) => {
      axiosInstance
        .get(`inventory-service/api/v1/product-rating/${productId}/mine/`)
        .then((res) => {
          if (res.data) {
            setMyReviews((prev) => ({ ...prev, [productId]: res.data }));
          }
        })
        .catch(() => {});
    });
  }, [orderDetails.order_items]);

  const saveRating = (productId, data) =>
    axiosInstance
      .post(`inventory-service/api/v1/product-rating/${productId}/`, data)
      .then((res) => {
        setMyReviews((prev) => ({ ...prev, [productId]: res.data }));
        setRatings((prev) => ({ ...prev, [productId]: res.data.rating }));
        return res;
      });

  const openReview = (item) => {
    const existing = myReviews[item.product_id];
    setReviewItem(item);
    setModalRating(ratings[item.product_id] || (existing ? existing.rating : 0));
    reset({ review: existing && existing.review ? existing.review : "" });
    setOpen(true);
  };

  const onSubmit = (data) => {
    if (!modalRating) {
      toast.warn("Please choose a star rating", TOAST);
      return;
    }
    setSubmitting(true);
    saveRating(reviewItem.product_id, { rating: modalRating, review: data.review })
      .then(() => {
        toast.success("Your review was saved", TOAST);
        setOpen(false);
      })
      .catch((error) => toast.error(errorMessage(error), TOAST))
      .finally(() => setSubmitting(false));
  };

  const quickSubmit = (item) => {
    const rating = ratings[item.product_id];
    if (!rating) {
      toast.warn("Tap the stars to choose a rating first", TOAST);
      return;
    }
    saveRating(item.product_id, { rating })
      .then(() => toast.success("Your rating was saved", TOAST))
      .catch((error) => toast.error(errorMessage(error), TOAST));
  };

  const [openRefund, setOpenRefund] = useState(false);
  const {
    register: register2,
    handleSubmit: handleSubmit2,
    reset: reset2,
  } = useForm();
  const onSubmit2 = (data) => {
    data.order_id = orderDetails.id;
    data.order_item_id = parseInt(productReviewData.id);

    // console.log(data)

    axiosInstance
      .post(baseURL + "orders/api/v1/orders/return-request/", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(function (response) {
        // console.log(response, "return")
        toast.success("Your return request added successfully", {
          position: "top-right",
          autoClose: 400,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      })
      .catch((error) => {
        console.log(error);
        // return error
        toast.error(error.message, {
          position: "top-right",
          autoClose: 400,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      });

    reset2();
  };

  console.log(orderDetails);
  return (
    <>
      <section className="container mx-auto space-y-4 px-3 sm:px-0">
        <div className="flex flex-col-reverse sm:flex-row justify-between sm:items-center   space-y-2 mx-2 ">
          <h2 className="text-[#282828] text-xs sm:text-sm font-semibold flex justify-start items-center gap-x-2 pt-3 sm:pt-0">
            Received Orders{" "}
            <ArrowRightIcon className="h-4 w-4 object-contain" /> Order{" "}
            <span className="text-[#3076CE]">
              #{orderDetails && orderDetails.order_id}
            </span>
          </h2>

          <Link
            to="/my-account"
            className="flex justify-end sm:items-center text-[#3076CE] text-xs sm:text-sm font-semibold "
          >
            <ArrowLeftIcon className="h-4 w-4 mx-1 object-contain" /> Back
          </Link>
        </div>

        {/* products */}
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center  space-y-2 mx-2 ">
            <h2 className="text-[#282828] text-sm font-semibold flex justify-start items-center gap-x-2">
              Package By
              <span className="text-[#3076CE]">
                {orderDetails.vendor_shopname}
              </span>
            </h2>

            <h2 className="text-[#3076CE] text-sm w-max font-semibold pb-1 border-b-2 px-4 border-[#0073FA]">
              View Invoice
            </h2>
          </div>
          {/* dummy products */}

          {orderDetails &&
            orderDetails.order_items?.map((item, index) => (
              <div
                className=" border border-gray-200 bg-white  rounded-md shadow px-2 sm:px-4 py-2 flex flex-col justify-between "
                key={index}
              >
                <div className="h-auto w-full flex flex-col sm:flex-row items-center justify-between gap-x-3 space-y-1 ">
                  <div className="flex items-center justify-start gap-x-3 h-full">
                    <figure>
                      <img
                        className="h-24 sm:h-36 w-24 sm:w-36 object-contain rounded-lg"
                        src={item.product_image_url}
                        alt="search product"
                      />
                    </figure>

                    <div className="flex flex-col justify-between space-y-2 sm:space-y-6">
                      <div className="flex flex-col justify-between space-y-1 sm:space-y-2">
                        <p className="text-gray-600 font-medium text-sm sm:text-base">
                          {item.product_title}
                          {/* <span className=' text-gray-400 font-normal'> by {orderDetails.vendor_shopname}</span> */}
                        </p>
                        <p className="text-gray-600 ">
                          ৳{" "}
                          <span className="text-[#3076CE]  font-semibold">
                            {item.total_price}
                          </span>
                        </p>
                      </div>

                      <p
                        className="text-red-500 font-medium cursor-pointer"
                        onClick={() => {
                          setProductReviewData(item);
                          setOpenRefund(true);
                        }}
                      >
                        Return
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 sm:space-y-4 w-full sm:w-max flex flex-col justify-center items-end ">
                    <div className="flex flex-col items-end gap-y-1">
                      <StarRating
                        value={
                          ratings[item.product_id] ||
                          (myReviews[item.product_id] ? myReviews[item.product_id].rating : 0)
                        }
                        onChange={(star) =>
                          setRatings((prev) => ({ ...prev, [item.product_id]: star }))
                        }
                      />
                      {myReviews[item.product_id] && (
                        <p className="text-xs text-gray-500">
                          You rated this {myReviews[item.product_id].rating}/5
                          {myReviews[item.product_id].review ? " and wrote a review" : ""}
                        </p>
                      )}
                    </div>
                    <div className=" flex flex-col sm:flex-row justify-center items-center gap-x-4">
                      <button
                        type="button"
                        onClick={() => openReview(item)}
                        className="whitespace-nowrap text-xs sm:text-sm bg-[#3076CE] text-white px-4 py-2 rounded-md border border-[#3076CE] w-full"
                      >
                        {myReviews[item.product_id] && myReviews[item.product_id].review
                          ? "Edit Your Review"
                          : "Write A review"}
                      </button>

                      <p className="text-[#3076CE]">Or</p>

                      <button
                        type="button"
                        onClick={() => quickSubmit(item)}
                        className="whitespace-nowrap w-full text-xs sm:text-sm text-[#3076CE] px-4 py-2 rounded-md border border-[#3076CE]"
                      >
                        {myReviews[item.product_id] ? "Update Rating" : "Submit Quick Rating"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>

        {/* track delivery */}

        <div className="space-y-3 border-t border-gray-300 pt-4 ">
          <div className=" border border-gray-200 bg-white  rounded-md shadow p-4 sm:p-8 flex flex-col justify-between">
            <OrderTimeline order={orderDetails} />
          </div>
        </div>

        {/* summary */}

        <div className=" space-y-2 sm:grid  grid-cols-12 gap-x-4 text-sm sm:border-t border-gray-300 sm:pt-4">
          <div className="col-span-10 border border-gray-200 bg-white  rounded-lg shadow p-4 flex flex-col justify-between">
            <div className=" grid grid-cols-8 items-start space-x-3">
              <div className="col-span-6">
                <span className="text-gray-700 font-semibold">
                  Shipping Address:{" "}
                </span>
                <span className="text-gray-500">
                  {orderDetails &&
                    `${orderDetails.shipping_address?.address}, ${orderDetails.shipping_address?.street}, ${orderDetails.shipping_address?.state}, ${orderDetails.shipping_address?.city} `}
                </span>
              </div>
              <div className="col-span-2 space-y-1">
                <div className="flex justify-end items-center gap-x-2">
                  <p className="text-gray-700 font-light">Payment Status :</p>
                  <div className="flex items-center text-sm font-bold text-[#22DA9B] ">
                    {orderDetails.payment_method == "cash" ? (
                      "Cash"
                    ) : (
                      <>
                        <svg
                          width="18"
                          height="15"
                          viewBox="0 0 12 11"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M5.60245 0.0831466C5.6575 0.0516977 5.71981 0.0351563 5.78322 0.0351562C5.84662 0.0351562 5.90893 0.0516977 5.96398 0.0831466L11.0663 2.99878C11.1221 3.03064 11.1685 3.07668 11.2007 3.13224C11.233 3.18779 11.25 3.25088 11.25 3.31512V3.83994C11.25 5.44789 10.7262 7.01208 9.75783 8.29572C8.78944 9.57936 7.42922 10.5126 5.88308 10.9541C5.8178 10.9727 5.74863 10.9727 5.68335 10.9541C4.13733 10.5124 2.77725 9.57909 1.8089 8.2955C0.840547 7.0119 0.316625 5.44783 0.316406 3.83994L0.316406 3.31512C0.316427 3.25088 0.333427 3.18779 0.365684 3.13224C0.397942 3.07668 0.44431 3.03064 0.500091 2.99878L5.60245 0.0831466ZM5.47124 7.84237L8.61867 3.907L8.05012 3.45216L5.36628 6.80586L3.46529 5.22195L2.99879 5.78175L5.47124 7.84237Z"
                            fill="url(#paint0_linear_2567_6147)"
                          />
                          <defs>
                            <linearGradient
                              id="paint0_linear_2567_6147"
                              x1="11.25"
                              y1="5.50159"
                              x2="-19.7191"
                              y2="16.3969"
                              gradientUnits="userSpaceOnUse"
                            >
                              <stop stopColor="#22DA9B" />
                              <stop offset="1" stopColor="#2995FC" />
                            </linearGradient>
                          </defs>
                        </svg>
                        Paid
                      </>
                    )}
                  </div>
                </div>
                <div className="flex justify-end items-center gap-x-2">
                  <span className="text-gray-700 font-light">Paid By </span>
                  <span className="text-gray-500">payment_method</span>
                </div>
              </div>
            </div>
            <div className=" grid grid-cols-8 items-start space-x-3">
              <div className="col-span-6 flex justify-start items-center gap-x-2">
                <figure>
                  <img
                    className="h-6 w-6 object-contain mx-auto"
                    src="/images/bangladesh.png"
                    alt="flag"
                  />
                </figure>
                <span className="text-gray-500">(+880) 1755-2121283</span>
              </div>
              <div className="col-span-2 ">
                <div className="flex justify-end items-center gap-x-2">
                  <span className="text-gray-700 font-light">
                    Order Status:{" "}
                  </span>
                  <span className="text-[#3076CE]"> {orderDetails.status}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-2 border border-gray-200 bg-white  rounded-lg shadow p-4 space-y-3">
            <p className="text-gray-700 text-center font-medium">
              {orderDetails.delivery_type == "REGULAR"
                ? "Regular Delivery"
                : "Express Delivery"}
            </p>
            <div className="space-y-2">
              <div className="flex justify-between items-center text-gray-500">
                <p>Subtotal</p>
                <p>{`৳ ${subtotal}`}</p>
              </div>
              <div className="flex justify-between items-center text-gray-500">
                <p>Shipping Fee</p>
                <p> {`৳ ${orderDetails.shipping_cost}`}</p>
              </div>
              <div className="flex justify-between items-center text-gray-500">
                <p>Discount</p>
                <p>{` -৳ ${orderDetails.discount}`}</p>
              </div>
            </div>
            <div className="flex justify-between items-center text-gray-500 border-t border-gray-300 pt-3">
              <p>Total</p>
              <p>
                {" "}
                ৳ <span className="text-[#3076CE]  font-semibold">{total}</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* review modal */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-40" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-8 space-y-4">
                  <div className="flex justify-between items-center border-b border-gray-300 pb-3">
                    <h3 className="text-base sm:text-xl text-gray-700 font-semibold">
                      Tell us what you feel. Write a review
                    </h3>

                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-500 hover:text-gray-600 focus:outline-none "
                      onClick={() => setOpen(false)}
                    >
                      <span className="sr-only">Close</span>
                      <XIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="flex flex-col gap-y-1 justify-center items-center">
                    <p className="flex flex-wrap gap-x-2 justify-center items-center text-base sm:text-lg font-medium">
                      <span>Order </span>
                      <span className="text-[#0073FA]">
                        {orderDetails.order_id}
                      </span>
                      <span>Package By </span>
                      <span className="text-[#0073FA]">
                        {" "}
                        {orderDetails.vendor_shopname}
                      </span>
                    </p>
                    {deliveredOn && (
                      <p className="text-sm sm:text-base text-gray-400">
                        Delivered on : {deliveredOn}
                      </p>
                    )}
                  </div>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-start items-center gap-x-3">
                        <figure className="h-24 w-24 p-1 drop-shadow rounded-md ">
                          <img
                            className="h-24 w-24 object-contain "
                            src={reviewItem ? reviewItem.product_image_url : ""}
                            alt=""
                          />
                        </figure>

                        <div className="space-y-2">
                          <div className="flex justify-start items-baseline gap-x-2 ">
                            <p className="text-sm sm:text-base text-[#0073FA] font-medium">
                              {reviewItem ? reviewItem.product_title : ""}
                            </p>
                            <p className="text-sm text-gray-400">(Click a star to rate)</p>
                            {modalRating ? (
                              <p className="text-sm font-medium text-[#0073FA]">({modalRating})</p>
                            ) : (
                              ""
                            )}
                          </div>
                          <StarRating value={modalRating} onChange={setModalRating} />
                        </div>
                      </div>

                      <div className="space-y-2 pt-2">
                        <p className="text-sm sm:text-base  sm:flex justify-start items-center gap-x-2">
                          <span className="text-gray-500">
                            Write something for{" "}
                          </span>{" "}
                          <span className="font-semibold text-gray-700">
                            “
                            {reviewItem ? reviewItem.product_title : ""}
                            ”
                          </span>
                        </p>

                        <form onSubmit={handleSubmit(onSubmit)}>
                          {/* review */}
                          <div className="form-group cmb-10">
                            <label className="pure-material-textfield-outlined">
                              <textarea
                                type="text"
                                rows="4"
                                cols="5"
                                placeholder=" "
                                {...register("review", { required: true })}
                              />

                              <span className="text-gray-400 font-medium">
                                Write a review
                              </span>
                            </label>
                          </div>
                          <div className="mt-5 sm:mt-4 sm:flex  justify-center ">
                            <button
                              type="submit"
                              disabled={submitting}
                              className="mt-3 inline-flex w-full justify-center rounded-md  bg-gradient-to-r from-[#2995FC] to-[#3076CE]  px-6 py-2 text-base font-medium text-white  sm:mt-0 sm:w-auto disabled:opacity-60"
                            >
                              {submitting ? "Saving..." : "Submit Review"}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* refund modal */}
      <Transition.Root show={openRefund} as={Fragment}>
        <Dialog as="div" className="relative z-40" onClose={setOpenRefund}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-8 space-y-4">
                  <div className="flex justify-between items-center border-b border-gray-300 pb-3">
                    <h3 className="text-base sm:text-xl text-gray-700 font-semibold">
                      Request for a Return / Refund
                    </h3>

                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-500 hover:text-gray-600 focus:outline-none "
                      onClick={() => setOpenRefund(false)}
                    >
                      <span className="sr-only">Close</span>
                      <XIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="flex flex-col gap-y-1 justify-center items-center">
                    <p className="flex flex-wrap gap-x-2 justify-center items-center text-base sm:text-lg font-medium">
                      <span>Order </span>
                      <span className="text-[#0073FA]">
                        {orderDetails.order_id}
                      </span>
                      <span>Package By </span>
                      <span className="text-[#0073FA]">
                        {" "}
                        {orderDetails.vendor_shopname}
                      </span>
                    </p>
                    {deliveredOn && (
                      <p className="text-sm sm:text-base text-gray-400">
                        Delivered on : {deliveredOn}
                      </p>
                    )}
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-start items-start gap-x-3">
                      <figure className="h-24 w-24 p-1 drop-shadow rounded-md ">
                        <img
                          className="h-24 w-24 object-contain "
                          src={
                            productReviewData
                              ? productReviewData.product_image_url
                              : ""
                          }
                          alt="sdfvdsv"
                        />
                      </figure>

                      <div className="space-y-2">
                        <p className="text-sm sm:text-base text-[#0073FA] font-medium">
                          {productReviewData
                            ? productReviewData.product_title
                            : "Name unavailabe"}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {/* <p className='text-sm sm:text-base  sm:flex justify-start items-center gap-x-2'>
													<span className='text-gray-500'>Write something for </span> <span className='font-semibold text-gray-700'>“ "No name available"”</span>
												</p> */}

                      <form onSubmit={handleSubmit2(onSubmit2)}>
                        {/* reason */}
                        <div className="form-group cmb-10">
                          <label className="pure-material-textfield-outlined">
                            <textarea
                              type="text"
                              rows="4"
                              cols="5"
                              placeholder=" "
                              {...register2("reason", { required: false })}
                            />

                            <span className="text-gray-400 font-medium">
                              Reason for Return
                            </span>
                          </label>
                        </div>

                        {/* <select id='location' name='location' className='mt-1 block w-full rounded-md border border-slate-300 shadow py-2 px-4 text-base font-normal text-gray-500 outline-none' {...register2("reason", {required: true})}>
													<option defaultValue className='text-gray-500'>
														I Want To Return The Product
													</option>
													<option value='cda' className='text-gray-500'>
														Canada
													</option>
													<option value='mxo' className='text-gray-500'>
														Mexico
													</option>
												</select> */}
                        <div className="mt-5 sm:mt-4 sm:flex  justify-center ">
                          <button
                            type="submit"
                            className="mt-3 inline-flex w-full justify-center rounded-md  bg-gradient-to-r from-[#2995FC] to-[#3076CE]  px-6 py-2 text-base font-medium text-white  sm:mt-0 sm:w-auto "
                            onClick={() => setOpenRefund(false)}
                          >
                            Submit Request
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <ToastContainer />
    </>
  );
}
